import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionLog } from "./logService";
import { getAiUsage, calculateAiCosts, AiUsageEntry } from "./aiUsageService";

// ── GEA BRAND PALETTE ─────────────────────────────────────────────────────────
const GEA_BLUE: [number, number, number] = [0, 0, 169];    // #0000a9
const GEA_SLATE: [number, number, number] = [30, 41, 59];   // slate-800
const GEA_LIGHT: [number, number, number] = [241, 245, 249]; // slate-100
const GEA_MID: [number, number, number] = [148, 163, 184]; // slate-400
const GEA_DARK_TXT: [number, number, number] = [15, 23, 42];   // slate-900
const WHITE: [number, number, number] = [255, 255, 255];

// Pricing reference (matching aiUsageService)
const PRICING: Record<string, { input: number, output: number }> = {
    'gemini-1.5-pro': { input: 3.50 / 1000000, output: 10.50 / 1000000 },
    'gemini-1.5-flash': { input: 0.075 / 1000000, output: 0.30 / 1000000 },
    'gemini-2.5-pro': { input: 4.00 / 1000000, output: 12.00 / 1000000 },
    'gemini-2.5-flash': { input: 0.10 / 1000000, output: 0.40 / 1000000 },
    'default': { input: 1.00 / 1000000, output: 3.00 / 1000000 }
};

export const generateAuditPdf = async (sessionLogs: SessionLog[]) => {
    try {
        const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
        const aiUsage = getAiUsage();
        const aiStats = calculateAiCosts(aiUsage);
        const reportId = `AUDIT-${new Date().getTime().toString().slice(-8)}`;
        const timestamp = new Date().toLocaleString();

        // ── 1. HEADER ──────────────────────────────────────────────────────────
        doc.setFillColor(...GEA_SLATE); doc.rect(0, 0, 210, 35, "F");
        doc.setFillColor(...GEA_BLUE); doc.rect(0, 0, 35, 35, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(...WHITE);
        doc.text("GEA", 17.5, 22, { align: "center" });

        doc.setFontSize(14);
        doc.text("SYSTEM AUDIT & AI USAGE REPORT", 45, 14);
        doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(...GEA_MID);
        doc.text(`REF: ${reportId}  |  GENERATED: ${timestamp}`, 45, 22);
        doc.text("NDT-CHECK PRO V6.0 SECURE AUDIT LOG", 45, 27);

        // ── 2. EXECUTIVE SUMMARY ────────────────────────────────────────────────
        doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(...GEA_BLUE);
        doc.text("1.0 OPERATIONAL SUMMARY", 15, 50);

        doc.setFillColor(...GEA_LIGHT); doc.rect(15, 53, 180, 25, "F");
        doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...GEA_SLATE);
        doc.text(`Total Sessions Recorded: ${sessionLogs.length}`, 20, 60);
        doc.text(`Total AI Requests Dispatched: ${aiUsage.length}`, 20, 66);
        doc.text(`Estimated Infrastructure Overhead: €${aiStats.totalCostEur.toFixed(4)} EUR (Excl. VAT)`, 20, 72);

        // ── 3. SESSION LOGS TABLE ───────────────────────────────────────────────
        doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(...GEA_BLUE);
        doc.text("2.0 USER ACCESSIBILITY LOGS", 15, 90);

        const sessionData = sessionLogs.map(log => [
            log.name,
            log.email,
            log.ip || 'Unknown',
            new Date(log.startTime).toLocaleString('en-GB', { hour12: false }),
            log.endTime ? new Date(log.endTime).toLocaleString('en-GB', { hour12: false }) : 'ACTIVE',
            log.durationMinutes ? `${log.durationMinutes}m` : '-'
        ]);

        autoTable(doc, {
            startY: 95,
            head: [['INSPECTOR', 'EMAIL ADDRESS', 'IP ADDR', 'LOGIN (UTC)', 'LOGOUT (UTC)', 'DUR.']],
            body: sessionData,
            headStyles: { fillColor: GEA_BLUE, textColor: WHITE, fontSize: 8, fontStyle: 'bold' },
            bodyStyles: { fontSize: 7, textColor: GEA_DARK_TXT },
            alternateRowStyles: { fillColor: GEA_LIGHT },
            margin: { left: 15, right: 15 },
            theme: 'grid'
        });

        // ── 4. AI USAGE TABLE ──────────────────────────────────────────────────
        let currentY = (doc as any).lastAutoTable.finalY + 15;
        if (currentY > 240) { doc.addPage(); currentY = 20; }

        doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(...GEA_BLUE);
        doc.text("3.0 NEURAL ENGINE RESOURCE CONSUMPTION", 15, currentY);

        // Group by model
        const perModel: Record<string, { count: number, input: number, output: number, costUsd: number }> = {};
        aiUsage.forEach(entry => {
            if (!perModel[entry.model]) perModel[entry.model] = { count: 0, input: 0, output: 0, costUsd: 0 };
            const p = PRICING[entry.model] || PRICING['default'];
            perModel[entry.model].count++;
            perModel[entry.model].input += entry.inputTokens;
            perModel[entry.model].output += entry.outputTokens;
            perModel[entry.model].costUsd += (entry.inputTokens * p.input) + (entry.outputTokens * p.output);
        });

        const usageData = Object.entries(perModel).map(([model, s]) => {
            const costEur = s.costUsd * aiStats.usdToEur;
            return [
                model,
                s.count.toString(),
                s.input.toLocaleString(),
                s.output.toLocaleString(),
                `€${costEur.toFixed(5)}`
            ];
        });

        autoTable(doc, {
            startY: currentY + 5,
            head: [['MODEL TIER', 'CALLS', 'INPUT TOKENS', 'OUTPUT TOKENS', 'COST (EUR)']],
            body: usageData,
            headStyles: { fillColor: GEA_BLUE, textColor: WHITE, fontSize: 8, fontStyle: 'bold' },
            bodyStyles: { fontSize: 7, textColor: GEA_DARK_TXT },
            alternateRowStyles: { fillColor: GEA_LIGHT },
            margin: { left: 15, right: 15 },
            theme: 'grid'
        });

        // ── 5. USER TRACEABILITY TABLE ──────────────────────────────────────────
        currentY = (doc as any).lastAutoTable.finalY + 15;
        if (currentY > 240) { doc.addPage(); currentY = 20; }

        doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(...GEA_BLUE);
        doc.text("4.0 TRACEABILITY: COST PER USER & IP", 15, currentY);

        const perUserIp: Record<string, { name: string, ip: string, calls: number, costUsd: number }> = {};
        aiUsage.forEach(entry => {
            const key = `${entry.userName || 'Unknown'}-${entry.ipAddress || 'Unknown'}`;
            if (!perUserIp[key]) {
                perUserIp[key] = {
                    name: entry.userName || 'Unknown',
                    ip: entry.ipAddress || 'Unknown',
                    calls: 0,
                    costUsd: 0
                };
            }
            const p = PRICING[entry.model] || PRICING['default'];
            perUserIp[key].calls++;
            perUserIp[key].costUsd += (entry.inputTokens * p.input) + (entry.outputTokens * p.output);
        });

        const userTraceData = Object.values(perUserIp).map(s => [
            s.name,
            s.ip,
            s.calls.toString(),
            `€${(s.costUsd * aiStats.usdToEur).toFixed(4)}`
        ]);

        autoTable(doc, {
            startY: currentY + 5,
            head: [['INSPECTOR NAME', 'IP ADDRESS', 'AI CALLS', 'ALLOCATED COST (EUR)']],
            body: userTraceData,
            headStyles: { fillColor: GEA_BLUE, textColor: WHITE, fontSize: 8, fontStyle: 'bold' },
            bodyStyles: { fontSize: 7, textColor: GEA_DARK_TXT },
            alternateRowStyles: { fillColor: GEA_LIGHT },
            margin: { left: 15, right: 15 },
            theme: 'grid'
        });

        // ── 6. BILLING TOTALS ───────────────────────────────────────────────────
        const endY = (doc as any).lastAutoTable.finalY + 12;
        doc.setFillColor(...GEA_SLATE); doc.rect(120, endY, 75, 28, "F");
        doc.setTextColor(...WHITE); doc.setFontSize(8); doc.setFont("helvetica", "normal");

        doc.text("TOTAL (EXCL. VAT):", 125, endY + 8);
        doc.text(`€${aiStats.totalCostEur.toFixed(4)} EUR`, 190, endY + 8, { align: 'right' });

        doc.text(`VAT (${(aiStats.vatRate * 100).toFixed(0)}%):`, 125, endY + 14);
        doc.text(`€${aiStats.totalVatEur.toFixed(4)} EUR`, 190, endY + 14, { align: 'right' });

        doc.setDrawColor(...GEA_MID); doc.setLineWidth(0.2); doc.line(125, endY + 17, 190, endY + 17);

        doc.setFont("helvetica", "bold"); doc.setFontSize(10);
        doc.text("TOTAL INV. (INCL. VAT):", 125, endY + 23);
        doc.text(`€${aiStats.totalWithVatEur.toFixed(4)} EUR`, 190, endY + 23, { align: 'right' });

        // ── 6. FOOTER ──────────────────────────────────────────────────────────
        const pageCount = (doc as any).internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFillColor(...GEA_LIGHT); doc.rect(0, 285, 210, 12, "F");
            doc.setFontSize(7); doc.setTextColor(...GEA_MID); doc.setFont("helvetica", "normal");
            doc.text("PROPRIETARY & CONFIDENTIAL — GEA GROUP NDT-CHECK PRO AUDIT RECORD", 15, 293);
            doc.setTextColor(...GEA_BLUE); doc.setFont("helvetica", "bold");
            doc.text(`PAGE ${i} OF ${pageCount}`, 195, 293, { align: "right" });
        }

        const pdfBlob = doc.output('blob');
        const url = URL.createObjectURL(pdfBlob);

        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
        console.error("PDF Audit generation failed:", err);
        alert("Failed to generate PDF Report. Please check the console for details.");
    }
};
